export interface UserMinimal {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface LocationReference {
  id: number;
  name: string;
}

export interface DrawingReference {
  id: string;
  drawing_number: string;
  title: string;
  discipline: string;
  label: string;
}

export interface TradeReference {
  id: string;
  name: string;
  color?: string;
  icon?: string;
}

export interface HotspotDetail {
  id: number;
  pitch: number;
  yaw: number;
  hfov: number;
  photo_id: number;
  scan_type?: 'Progress' | 'Asbuild' | 'Corridor';
}

export type RFIStatus = 'DRAFT' | 'OPEN' | 'PENDING_RESPONSE' | 'ANSWERED' | 'CLOSED';
export type RFIPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type RFIImpact = 'NONE' | 'POTENTIAL' | 'YES';
export type RFIBallInCourt = 'requested_by' | 'responsible_party' | 'closed';

export interface RFIComment {
  id: string;
  rfi: string;
  author: UserMinimal | null;
  body: string;
  is_official_response: boolean;
  created_at: string;
  updated_at: string;
}

export interface RFIAttachment {
  id: string;
  rfi: string;
  file: string;
  file_url: string | null;
  uploaded_by: UserMinimal | null;
  created_at: string;
}

export interface RFIStatusHistoryEntry {
  id: string;
  from_status: RFIStatus | '';
  to_status: RFIStatus;
  changed_by: UserMinimal | null;
  note: string;
  created_at: string;
}

export interface RFIAuditLogEntry {
  id: string;
  action: string;
  actor: UserMinimal | null;
  message: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface RFISummary {
  id: string;
  project: number;
  rfi_number: string;
  title: string;
  status: RFIStatus;
  priority: RFIPriority;
  requested_by_user: UserMinimal | null;
  responsible_party_user: UserMinimal | null;
  assignee_user: UserMinimal | null;
  created_by: UserMinimal | null;
  due_date: string | null;
  drawing_reference: DrawingReference | null;
  trade: TradeReference | null;
  hotspot_id: number | null;
  is_overdue: boolean;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface RFIItem extends RFISummary {
  sequence_number: number;
  question: string;
  cost_impact: RFIImpact;
  schedule_impact: RFIImpact;
  location_reference: LocationReference | null;
  hotspot_detail: HotspotDetail | null;
  spec_section_reference: string;
  suggested_solution: string;
  official_response: string;
  response_date: string | null;
  closed_at: string | null;
  ball_in_court: RFIBallInCourt;
  comments: RFIComment[];
  attachments: RFIAttachment[];
  status_history: RFIStatusHistoryEntry[];
  audit_logs: RFIAuditLogEntry[];
}

export interface CreateRFIData {
  project: number;
  title: string;
  question: string;
  requested_by_user?: number | null;
  responsible_party_user?: number | null;
  assignee_user?: number | null;
  status?: RFIStatus;
  priority?: RFIPriority;
  due_date?: string | null;
  cost_impact?: RFIImpact;
  schedule_impact?: RFIImpact;
  location_reference?: number | null;
  drawing_reference?: string | null;
  trade?: string | null;
  hotspot?: number | null;
  spec_section_reference?: string;
  suggested_solution?: string;
  official_response?: string;
}

export type UpdateRFIData = Partial<CreateRFIData>;

export interface RFIFilters {
  status?: RFIStatus;
  priority?: RFIPriority;
  responsible_party?: number;
  assignee?: number;
  overdue?: boolean;
  search?: string;
  hotspot_id?: number;
  photo_id?: number;
}

export interface RFIStatusSummaryEntry {
  status: RFIStatus;
  count: number;
}

export interface RFIWidgetCounts {
  open_rfis: number;
  overdue_rfis: number;
  awaiting_my_response: number;
}

export type RFISortKey =
  | 'rfi_number'
  | 'status'
  | 'priority'
  | 'responsible_party'
  | 'due_date'
  | 'created_at';

export interface RFISortState {
  key: RFISortKey;
  direction: 'asc' | 'desc';
}

export const RFI_STATUS_LABELS: Record<RFIStatus, string> = {
  DRAFT: 'Draft',
  OPEN: 'Open',
  PENDING_RESPONSE: 'Pending Response',
  ANSWERED: 'Answered',
  CLOSED: 'Closed',
};

export const RFI_PRIORITY_LABELS: Record<RFIPriority, string> = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
};

export const RFI_IMPACT_LABELS: Record<RFIImpact, string> = {
  NONE: 'None',
  POTENTIAL: 'Potential',
  YES: 'Yes',
};
